'use client';

import type { ReactNode } from 'react';
import styles from './label.module.css';

export type LabelProps = {
  children: ReactNode;
  /**
   * Id of the control this names. Only meaningful on the default `label`
   * element, and only when the target is a labelable element (`input`,
   * `textarea`, `select`, `button`). Pointing it at a `div`-based custom
   * trigger does nothing at all — use `as="span"` plus `id` for those.
   */
  htmlFor?: string;
  /**
   * Renders the element only. `label` is right when a native control is being
   * named; `span` is right when the control is a custom one that names itself
   * with `aria-labelledby` pointing at this element's `id`.
   *
   * The distinction is load-bearing, not cosmetic: a `label` wrapping or
   * pointing at a non-labelable element gives the control NO accessible name,
   * while nesting one `label` inside another is invalid HTML — which is what
   * FormField callers were doing when they passed `<label htmlFor>` as the
   * `label` prop.
   */
  as?: 'label' | 'span';
  /** Appends the required asterisk. */
  required?: boolean;
  /** Dims the label to match a disabled control. Purely visual. */
  disabled?: boolean;
  id?: string;
  className?: string;
  /**
   * `'sm'` (default) is the field-label treatment this component exists for —
   * see the type-level doc comment below for why it is pinned rather than
   * borrowed from `Typography`'s scale. `'lg'` matches `heading-sm` (size,
   * weight, letter-spacing and primary colour) for the rarer case where a
   * caller's surrounding fields already render at that size and a lone
   * default-size label would be the odd one out — prefer `'sm'` whenever a
   * field is free to look however this component says a field should look.
   */
  size?: 'sm' | 'lg';
};

/**
 * The product's field label.
 *
 * It exists because the label treatment was previously hard-coded inside
 * `form-field.module.css` and nowhere else, so anything that needed a label
 * outside a `FormField` — a Switch row, a checkbox group caption, a filter
 * panel heading — re-declared `font-size: 12px; font-weight: 600` and picked
 * its own grey. `FormField` now renders this, which is what makes every input
 * in the system share one label rather than one *copy* of a label.
 *
 * The type is declared here rather than borrowed from `Typography`'s scale on
 * purpose: the nearest scale step (`body-xs-semibold`) resolves to weight 480,
 * and a label is 600. Routing it through the scale would silently lighten every
 * label in the product.
 */
export function Label({
  children,
  htmlFor,
  as = 'label',
  required = false,
  disabled = false,
  id,
  className,
  size = 'sm',
}: Readonly<LabelProps>) {
  const Component = as;
  const classNames = [
    styles.label,
    size === 'lg' ? styles.sizeLg : '',
    disabled ? styles.disabled : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <Component
      id={id}
      className={classNames}
      htmlFor={as === 'label' ? htmlFor : undefined}
      data-required={required ? 'true' : undefined}
    >
      {children}
      {required ? (
        <>
          {/* The glyph is decoration and is hidden from assistive tech, which
              is also what the old `content: '*'` pseudo-element amounted to.
              The difference is the word beside it: previously "required" was
              conveyed by a red asterisk and nothing else, so it did not exist
              for a screen-reader user unless the control itself also carried
              `aria-required`, and most of them did not. */}
          <span className={styles.asterisk} aria-hidden="true">
            *
          </span>
          <span className={styles.srOnly}>(required)</span>
        </>
      ) : null}
    </Component>
  );
}
